import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaGripLines, FaSearch } from "react-icons/fa";
import axios from "axios";
import { RxCrossCircled } from "react-icons/rx";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdLockOutline } from "react-icons/md";
import { Link } from "react-router-dom";

function ModifyCardDrag({
  cards,
  setCards,
  setShowCardColumns,
  setTableVisible,
  isPerformancePage = false,
}) {
  const [tempCards, setTempCards] = useState(() =>
    JSON.parse(JSON.stringify(cards)),
  );
  const [columnSearchString, setColumnSearchString] = useState("");
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => cancelAnimationFrame(animation);
  }, []);

  useEffect(() => {
    setTempCards(JSON.parse(JSON.stringify(cards)));
  }, [cards]);

  const uniqueCategories = Array.from(
    new Set(tempCards.map((card) => card.category)),
  );
  const categoriesToMoveDown = ["Custom cards", "Budget Name"];
  const orderedCategories = [
    ...uniqueCategories.filter((cat) => !categoriesToMoveDown.includes(cat)),
    ...uniqueCategories.filter((cat) => categoriesToMoveDown.includes(cat)),
  ];

  useEffect(() => {
    if (orderedCategories.length > 0 && expandedCategories.length === 0) {
      setExpandedCategories([orderedCategories[0]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCategory = (category) => {
    setExpandedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  const ColumnItem = ({ card }) => {
    const isDisabled = card.locked;

    return (
      <div
        className={`flex items-center justify-between p-2 rounded transition-colors group ${
          isDisabled
            ? "opacity-60 cursor-not-allowed"
            : "cursor-pointer hover:bg-blue-50"
        }`}
        onClick={() => {
          if (!isDisabled) toggleColumnVisibility(card.key);
        }}
      >
        <label
          className="flex items-center space-x-3 w-full cursor-inherit"
          onClick={(e) => e.preventDefault()}
        >
          <input
            type="checkbox"
            checked={card.visible}
            disabled={isDisabled}
            readOnly
            className={`w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 pointer-events-none ${
              isDisabled ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          />
          <span className="text-sm select-none text-gray-700 group-hover:text-gray-900">
            {card.title}
          </span>
        </label>

        {card.category === "Custom cards" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              deleteCustomColumn(card.id, card.title);
            }}
            className="text-gray-400 hover:text-red-600 p-1 shrink-0"
          >
            <RiDeleteBin6Line size={15} />
          </button>
        )}
      </div>
    );
  };

  const deleteCustomColumn = (id, name) => {
    if (window.confirm(`Delete ${name} Column?`)) {
      axios
        .post(
          "https://api.confidanto.com/custom_columns/delete-custom-column",
          {
            email: localStorage.getItem("email"),
            id: id,
          },
        )
        .then(() => {
          setTempCards((prev) => prev.filter((col) => col.id !== id));
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const visibleCards = tempCards.filter((card) => card.visible);
    const hiddenCards = tempCards.filter((card) => !card.visible);

    const [moved] = visibleCards.splice(source.index, 1);
    visibleCards.splice(destination.index, 0, moved);

    setTempCards([...visibleCards, ...hiddenCards]);
  };

  const toggleColumnVisibility = (key) => {
    setTempCards(
      tempCards.map((card) =>
        card.key === key && !card.locked
          ? { ...card, visible: !card.visible }
          : card,
      ),
    );
  };

  const applyChanges = () => {
    setCards(tempCards);
    setShowCardColumns(false);
    setTableVisible(true);
  };

  const cancelChanges = () => {
    setShowCardColumns(false);
    setTableVisible(true);
  };

  const filteredCards = tempCards.filter(
    (item) => !item.key.includes("_previous_percentage"),
  );
  const visibleCardsCount = tempCards.filter((card) => card.visible).length;

  const stopPropagation = (e) => {
    e.stopPropagation();
    if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  if (!enabled) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex justify-end"
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
    >
      <div
        className="absolute inset-0 bg-black/60 transition-opacity cursor-pointer"
        onClick={cancelChanges}
      ></div>

      <div className="relative w-full md:w-[75vw] lg:w-[65vw] max-w-5xl h-full bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out animate-in slide-in-from-right">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0 bg-white">
          <div className="flex items-center">
            <button
              onClick={cancelChanges}
              className="mr-4 text-gray-500 hover:text-gray-800 transition-colors"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-xl font-normal text-gray-800">Modify cards</h2>
          </div>
          <Link
            to={
              isPerformancePage ? "/performance-custom-column" : "/custom-column"
            }
            className="flex items-center space-x-1 rounded-md text-blue-600 px-3 py-1.5 text-sm font-medium hover:bg-blue-50 transition-colors"
          >
            <span>+ Custom Column</span>
          </Link>
        </div>

        <div className="flex flex-1 overflow-hidden bg-white">
          <div className="flex-1 flex flex-col border-r border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 shrink-0">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search cards..."
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all"
                  value={columnSearchString}
                  onChange={(e) =>
                    setColumnSearchString(e.target.value.toLowerCase())
                  }
                />
                <FaSearch className="absolute left-3.5 top-3.5 text-gray-400" />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {orderedCategories.map((category) => {
                const categoryCards = filteredCards.filter(
                  (card) => card.category === category,
                );
                const searchedCards = categoryCards.filter((card) =>
                  card.title.toLowerCase().includes(columnSearchString),
                );

                if (columnSearchString && searchedCards.length === 0) return null;
                const isExpanded =
                  expandedCategories.includes(category) ||
                  columnSearchString !== "";

                return (
                  <div key={category} className="border-b border-gray-100">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 focus:outline-none transition-colors"
                    >
                      <span className="font-medium text-gray-700 text-sm">
                        {category}
                      </span>
                      <svg
                        className={`text-gray-400 transform transition-transform duration-200 ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>

                    {isExpanded && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-2 gap-x-4 px-6 pb-5 pt-1">
                        {searchedCards.map((card) => (
                          <ColumnItem key={card.id || card.key} card={card} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="w-[35%] min-w-[300px] shrink-0 flex flex-col bg-gray-50/50">
            <div className="px-6 py-4 border-b border-gray-200 shrink-0">
              <h3 className="font-semibold text-gray-800 text-sm">
                Your cards ({visibleCardsCount})
              </h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Drag and drop to reorder
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3">
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="cardsList">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="space-y-2"
                    >
                      {tempCards
                        .filter((card) => card.visible)
                        .map((card, index) => (
                          <Draggable
                            key={card.id || card.key}
                            draggableId={String(card.id || card.key)}
                            index={index}
                            isDragDisabled={card.locked}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`flex items-center justify-between px-3 py-2.5 bg-white border ${
                                  snapshot.isDragging
                                    ? "border-blue-400 shadow-md"
                                    : "border-gray-200 shadow-sm"
                                } rounded-md group hover:border-blue-300 transition-colors`}
                              >
                                <div className="flex items-center space-x-3 overflow-hidden">
                                  {!card.locked ? (
                                    <FaGripLines className="text-gray-400 cursor-grab shrink-0 hover:text-gray-600" />
                                  ) : (
                                    <MdLockOutline className="text-gray-400 shrink-0" />
                                  )}
                                  <span className="text-sm text-gray-700 truncate">
                                    {card.title}
                                  </span>
                                </div>
                                {!card.locked && (
                                  <button
                                    onClick={() => toggleColumnVisibility(card.key)}
                                    className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors"
                                    title="Hide card"
                                  >
                                    <RxCrossCircled size={16} />
                                  </button>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>

            {/* Save Column Set Checkbox (disabled for now) */}
            {/*
            <div className="p-4 border-t border-gray-200 bg-gray-50 shrink-0">
              <label className="flex items-center space-x-2 cursor-pointer group">
                <input
                  type="checkbox"
                  id="saveColumnSet"
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">
                  Save your column set (name required)
                </span>
              </label>
            </div>
            */}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center space-x-3 bg-white shrink-0">
          <button
            onClick={applyChanges}
            className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            Apply
          </button>
          <button
            onClick={cancelChanges}
            className="px-6 py-2.5 text-blue-600 bg-transparent text-sm font-medium rounded-lg hover:bg-blue-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ModifyCardDrag;
